import type { FC, ReactNode } from 'react'
import type { PaintingAsset } from '@/types'

import Image from 'next/image'
import { cn } from '@/lib/utils'

interface DetailCropProps {
    painting: PaintingAsset
    /** Point to zoom into, as fractions of the painting's width and height (0 to 1). */
    focus: [number, number]
    /** How far to zoom in, relative to the painting filling the frame. */
    zoom: number
    /** Frame shape as width divided by height. */
    aspect?: number
    caption: ReactNode
    sizes?: string
    className?: string
}

/*
 * Shows a zoomed-in crop of a painting with a small locator that marks
 * where the detail sits in the full picture.
 */
const DetailCrop: FC<DetailCropProps> = ({
    painting,
    focus,
    zoom,
    aspect = 4 / 5,
    caption,
    sizes = '(min-width: 1024px) 1400px, 160vw',
    className
}): ReactNode => {
    const [focusX, focusY] = focus
    const paintingAspect = painting.width / painting.height

    // Share of the painting visible in the frame before zooming (object-fit: cover).
    const coverWidth = paintingAspect > aspect ? aspect / paintingAspect : 1
    const coverHeight = paintingAspect > aspect ? 1 : paintingAspect / aspect

    const windowWidth = coverWidth / zoom
    const windowHeight = coverHeight / zoom

    const locator = {
        left: `${focusX * (1 - windowWidth) * 100}%`,
        top: `${focusY * (1 - windowHeight) * 100}%`,
        width: `${windowWidth * 100}%`,
        height: `${windowHeight * 100}%`
    }

    const origin = `${focusX * 100}% ${focusY * 100}%`

    return (
        <figure className={cn('group', className)}>
            <div
                className='relative overflow-hidden rounded-sm bg-sepia shadow-painting transition-shadow duration-700 ease-out group-hover:shadow-painting-glow'
                style={{ aspectRatio: String(aspect) }}
            >
                <div
                    className='absolute inset-0 transition-transform duration-[1600ms] ease-out group-hover:scale-[1.06] motion-reduce:transition-none motion-reduce:group-hover:scale-100'
                    style={{ transformOrigin: origin }}
                >
                    <Image
                        src={painting.src}
                        alt={`Close-up detail of ${painting.title}, ${painting.year}`}
                        fill
                        sizes={sizes}
                        className='object-cover'
                        style={{ objectPosition: origin, transform: `scale(${zoom})`, transformOrigin: origin }}
                    />
                </div>

                <div aria-hidden='true' className='pointer-events-none absolute inset-0 shadow-[inset_0_0_90px_rgb(10_8_6/0.7)]' />

                <span className='absolute right-4 top-4 rounded-full bg-ink/70 px-3 py-1 text-[0.6rem] font-medium uppercase tracking-[0.3em] text-gold backdrop-blur-sm'>
                    Detail
                </span>

                <div aria-hidden='true' className='absolute bottom-4 left-4 w-16 overflow-hidden rounded-[2px] shadow-soft ring-1 ring-parchment/25 sm:w-20'>
                    <div className='relative' style={{ aspectRatio: `${painting.width} / ${painting.height}` }}>
                        <Image
                            src={painting.src}
                            alt=''
                            fill
                            sizes='80px'
                            className='object-cover opacity-75'
                        />

                        <span className='absolute rounded-[1px] ring-1 ring-gold shadow-glow' style={locator} />
                    </div>
                </div>
            </div>

            <figcaption className='mt-4 text-sm leading-relaxed text-parchment/65'>
                {caption}
            </figcaption>
        </figure>
    )
}

export default DetailCrop
